import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { BaseProductSet, ProductCart, ProductCartItem, ProductSet, ProductSetInventory, ProductSetOrder } from 'shared';

const initialState: FurnitureAndEquipmentState = {
  productSets: null,
  baseProductSets: null,
  productCart: null,
  productSetOrders: null,
  productSetInventory: null
};

export type FurnitureAndEquipmentState = {
  productSets: ProductSet[] | null;
  baseProductSets: BaseProductSet[] | null;
  productCart: ProductCart | null;
  productSetOrders: ProductSetOrder[] | null;
  productSetInventory: ProductSetInventory[] | null;
};

const furnitureAndEquipmentSlice = createSlice({
  name: 'FurnitureAndEquipment',
  initialState,
  reducers: {
    setProductSets: (state, action: PayloadAction<ProductSet[]>) => {
      state.productSets = action.payload;
    },
    setBaseProductSets: (state, action: PayloadAction<BaseProductSet[]>) => {
      state.baseProductSets = action.payload;
    },
    setProductCart: (state, action: PayloadAction<ProductCart>) => {
      state.productCart = action.payload;
    },
    addProductRentalCartItem: (state, action: PayloadAction<ProductCartItem>) => {
      if (!state.productCart) return;

      state.productCart.rental_items.push({ ...action.payload, type: 'rental' });
    },
    updateProductRentalCartItem: (state, action: PayloadAction<ProductCartItem>) => {
      if (!state.productCart) return;

      state.productCart.rental_items = state.productCart.rental_items.map(item =>
        item.id === action.payload.id ? { ...action.payload, type: 'rental' } : item
      );
    },
    removeProductRentalCartItem: (state, action: PayloadAction<{ id: number }>) => {
      if (!state.productCart) return;

      state.productCart.rental_items = state.productCart.rental_items.filter(item => item.id !== action.payload.id);
    },
    setProductSetOrders: (state, action: PayloadAction<ProductSetOrder[]>) => {
      state.productSetOrders = action.payload;
    },
    addProductSetOrder: (state, action: PayloadAction<ProductSetOrder>) => {
      if (!state.productSetOrders) return;

      state.productSetOrders.unshift(action.payload);
    },
    setProductSetInventory: (state, action: PayloadAction<ProductSetInventory[]>) => {
      state.productSetInventory = action.payload;
    },
    addProductSetInventoryItems: (state, action: PayloadAction<ProductSetInventory[]>) => {
      if (!state.productSetInventory) return;

      const inventory = state.productSetInventory;

      action.payload.forEach(item => {
        // if the product set is already in the inventory, increment the quantities
        if (item.product_set.id in inventory) {
          inventory[item.product_set.id].rental_quantity += item.rental_quantity;
          inventory[item.product_set.id].purchase_quantity += item.purchase_quantity;
        } else {
          inventory[item.product_set.id] = {
            product_set: item.product_set,
            rental_quantity: item.rental_quantity,
            purchase_quantity: item.purchase_quantity
          };
        }
      });
    },
    setHasGivenFeedback: (state, action: PayloadAction<{ id: number; product_set_order_id: number }>) => {
      if (!state.productSetOrders) return;
      const order = state.productSetOrders.find(order => order.id === action.payload.product_set_order_id);
      if (!order) return;

      const group = order.product_set_rental_groups.find(group => group.id === action.payload.id);
      if (!group) return;

      group.has_given_feedback = true;
    },
    setHasGivenFurniturePurchaseInquiry: (state, action: PayloadAction<{ id: number }>) => {
      if (!state.productSets) return;

      const productSet = state.productSets.find(set => set.id === action.payload.id);
      if (!productSet) return;

      productSet.has_given_furniture_purchase_inquiry = true;
    }
  }
});

export const { actions } = furnitureAndEquipmentSlice;
export default furnitureAndEquipmentSlice.reducer;
